import { NotifyObject, NotifyUpdateObject } from 'semantic-math-editor';
import {Editor} from './editor';

export enum NavigationDirection {
  left = "left",
  right = "right",
  home = "home",
  end = "end",
  up = "up",
  down = "down"
}

export class EditorManager {
 
  //DIV ID to EDITOR
  private readonly editorMap = new Map<string, Editor>();
  private readonly onDelete: () => void;
  private readonly onMoveOut:(editor:Editor,del:boolean,dir:NavigationDirection)=>void;
  private readonly onCursorPlace: (editor: Editor) => void;
  private readonly noErrDescription: string;
  private readonly errorDiv: ()=>Element;

  constructor(onDelete: () => void, onMoveOut: (editor: Editor, del: boolean, dir: NavigationDirection) => void,
              onCursorPlace: (editor: Editor) => void, errorDiv: () => Element, noErrDescription: string) {
    this.onDelete = onDelete;
    this.errorDiv = errorDiv;
    this.noErrDescription = noErrDescription;
    this.onMoveOut = onMoveOut;
    this.onCursorPlace = onCursorPlace;
  }

  clear(): void {
    this.editorMap.forEach((editor) => {
      editor.delete();
      editor.element.remove();
    });
    this.editorMap.clear();
  }

  isEditor(node: Node): boolean {
    const el = node as Element;
    if (el) {
      return this.editorMap.has(el.id);
    }
    return false;
  }

  getEditor(id: string): Editor | undefined {
    return this.editorMap.get(id);
  }

  getEditorByNode(node: Node): Editor|undefined {
    let ret;
    this.editorMap.forEach(editor => {
      if (editor.element.contains(node)) {
        ret = editor;
      }
    });
    return ret;
  }

  forEach(cb: (editor: Editor) => void) {
    this.editorMap.forEach(cb);
  }

  find(callback: (editor: Editor) => boolean): Editor | null {
    let result: Editor | null = null;
    this.editorMap.forEach(editor => {
      if (callback(editor)) {
        result = editor;
      }
    });
    return result;
}

  createNewEditor(id: string, readonly: boolean,wrap:boolean): Editor {

    const node = this.createNewEditorDiv(id);

    const editor = new Editor(node, this.errorDiv(), this.noErrDescription,!!wrap);
    if (readonly) {
      editor.readonly = readonly;
    }
    else {
      node.addEventListener("touchstart",()=>{editor.showMobileKeyboard();});
    }

    editor.onUpdate = cb => {
      if (cb.indexOf(NotifyUpdateObject.cursor) >= 0 && !editor.hasSelection) {
        this.onCursorPlace(editor);
      }
    };

    editor.onNotify = cb => {
      const range: Range | null = null;
      if (
        cb.indexOf( NotifyObject.failedNavigationLeft) >= 0 ||
        cb.indexOf(NotifyObject.failedBackspace) >= 0 ||
        cb.indexOf(NotifyObject.empty) >= 0
      ) {
        this.onMoveOut(editor, cb.indexOf(NotifyObject.failedBackspace) >= 0 || cb.indexOf(NotifyObject.empty) >= 0,
          NavigationDirection.left);
      } else if (
        cb.indexOf(NotifyObject.failedNavigationRight) >= 0 ||
        cb.indexOf(NotifyObject.failedDelete) >= 0
      ) {
        this.onMoveOut(editor,cb.indexOf(NotifyObject.failedDelete)>=0,NavigationDirection.right);
      } else if (cb.indexOf(NotifyObject.failedNavigationHome) >= 0) {
        const parent = editor.element.parentElement;
        if (parent && parent.childNodes.length > 0) {
          this.onMoveOut(editor,false,NavigationDirection.home);
        }
      } else if (cb.indexOf(NotifyObject.failedNavigationEnd) >= 0) {
        const parent = editor.element.parentElement;
        if (parent && parent.childNodes.length > 0) {
          this.onMoveOut(editor,false,NavigationDirection.end);
        }
      } else if(cb.indexOf(NotifyObject.failedNavigationUp) >= 0) {
        this.onMoveOut(editor,false,NavigationDirection.up);
      } else if(cb.indexOf(NotifyObject.failedNavigationDown) >= 0) {
        this.onMoveOut(editor,false,NavigationDirection.down);
      }

      if (
        cb.indexOf(NotifyObject.failedBackspace) >= 0 ||
        cb.indexOf(NotifyObject.failedDelete) >= 0 ||
        cb.indexOf(NotifyObject.empty) >= 0
      ) {
        if (editor.empty) {
          //Empty tree - remove the editor
          this.delete(editor);
        }
      }
    };

    this.editorMap.set(id, editor);
    return editor;
  }

  delete(editor: Editor): any {
    const id = editor.element.id;
    editor.delete();
    editor.element.remove();
    this.editorMap.delete(id);
    this.onDelete();
  }

  private createNewEditorDiv(id: string): HTMLDivElement {
    const node = document.createElement("div");
    node.setAttribute("tabindex", "0");
    node.setAttribute("contenteditable", "false");
    node.setAttribute("class", "vue-matharia");
    node.setAttribute("id", id);
    return node;
  }
}
